/**
 * Tenant Analytics — CTA clicks, form submissions, and page views over time.
 * Time-series area charts, drill-down modal with the raw events for any day,
 * saved filter presets (per-user), and CSV export.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { listTenantBrandings, type TenantBranding } from "@/lib/tenant-branding";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar as RBar } from "recharts";
import {
  BarChart3, Download, Filter, MousePointerClick, Send, Eye, Sparkles,
  Bookmark, BookmarkPlus, Trash2, Search, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

type EventRow = {
  id: string;
  event_name: string;
  path: string | null;
  properties: Record<string, any>;
  session_id: string | null;
  created_at: string;
};

type Preset = { id: string; name: string; filters: any; is_shared: boolean; owner_id: string };

const TRACKED_EVENTS = ["cta_click", "form_submit", "landing_view", "hero_view", "pricing_section_view", "compliance_section_view"];

export function TenantAnalyticsConsole() {
  const qc = useQueryClient();
  const { data: tenants = [] } = useQuery({ queryKey: ["tenant_brandings"], queryFn: listTenantBrandings });
  const [tenantSlug, setTenantSlug] = useState<string>("__all");
  const [range, setRange] = useState<"7d" | "30d" | "90d">("30d");
  const [routeFilter, setRouteFilter] = useState("");
  const [variantFilter, setVariantFilter] = useState("");
  const [eventFilter, setEventFilter] = useState<string>("__all");
  const [drill, setDrill] = useState<{ day: string; events: EventRow[] } | null>(null);
  const [detailEvent, setDetailEvent] = useState<EventRow | null>(null);

  const sinceIso = useMemo(() => {
    const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
    return new Date(Date.now() - days * 864e5).toISOString();
  }, [range]);

  const { data: events = [], isLoading, refetch } = useQuery({
    queryKey: ["tenant-analytics", tenantSlug, range, eventFilter],
    queryFn: async () => {
      let q = supabase
        .from("analytics_events")
        .select("id,event_name,path,properties,session_id,created_at")
        .gte("created_at", sinceIso)
        .in("event_name", eventFilter === "__all" ? TRACKED_EVENTS : [eventFilter])
        .order("created_at", { ascending: true })
        .limit(5000);
      if (tenantSlug !== "__all") q = q.contains("properties", { tenant_slug: tenantSlug });
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as EventRow[];
    },
  });

  const { data: presets = [] } = useQuery({
    queryKey: ["analytics-presets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("analytics_filter_presets" as never)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) return [];
      return (data as Preset[]) ?? [];
    },
  });

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (routeFilter && !(e.path ?? "").toLowerCase().includes(routeFilter.toLowerCase())) return false;
      if (variantFilter) {
        const v = String(e.properties?.variant ?? e.properties?.cta ?? "").toLowerCase();
        if (!v.includes(variantFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [events, routeFilter, variantFilter]);

  const summary = useMemo(() => {
    const cta = filtered.filter((e) => e.event_name === "cta_click").length;
    const forms = filtered.filter((e) => e.event_name === "form_submit").length;
    const views = filtered.filter((e) => e.event_name.endsWith("_view")).length;
    const sessions = new Set(filtered.map((e) => e.session_id ?? e.id)).size;
    const conv = views ? Math.round(((cta + forms) / views) * 1000) / 10 : 0;
    return { cta, forms, views, conv, sessions };
  }, [filtered]);

  const series = useMemo(() => {
    const bucket = new Map<string, { day: string; cta: number; forms: number; views: number }>();
    for (const e of filtered) {
      const day = e.created_at.slice(0, 10);
      const b = bucket.get(day) ?? { day, cta: 0, forms: 0, views: 0 };
      if (e.event_name === "cta_click") b.cta++;
      else if (e.event_name === "form_submit") b.forms++;
      else if (e.event_name.endsWith("_view")) b.views++;
      bucket.set(day, b);
    }
    return Array.from(bucket.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [filtered]);

  const hourly = useMemo(() => {
    const arr = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}:00`, events: 0 }));
    for (const e of filtered) {
      const h = new Date(e.created_at).getHours();
      arr[h].events++;
    }
    return arr;
  }, [filtered]);

  const topRoutes = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of filtered) {
      const p = e.path ?? "(unknown)";
      map.set(p, (map.get(p) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [filtered]);

  const topVariants = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of filtered) {
      const v = String(e.properties?.variant ?? e.properties?.cta ?? "").trim();
      if (!v) continue;
      map.set(v, (map.get(v) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [filtered]);

  const drillDown = (day: string) => {
    const evs = filtered.filter((e) => e.created_at.slice(0, 10) === day).sort((a, b) => a.created_at.localeCompare(b.created_at));
    setDrill({ day, events: evs });
  };

  const exportCsv = () => {
    if (!filtered.length) return toast.error("Nothing to export.");
    const headers = ["created_at", "event_name", "path", "tenant_slug", "variant", "cta", "session_id", "properties"];
    const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    for (const e of filtered) {
      lines.push([
        e.created_at, e.event_name, e.path,
        e.properties?.tenant_slug, e.properties?.variant, e.properties?.cta, e.session_id,
        JSON.stringify(e.properties ?? {}),
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tenant-analytics-${tenantSlug}-${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  const savePreset = async () => {
    const name = window.prompt("Preset name?");
    if (!name) return;
    try {
      const { error } = await supabase.rpc("save_analytics_preset" as never, {
        _name: name,
        _filters: { tenantSlug, range, routeFilter, variantFilter, eventFilter },
        _shared: false,
      } as never);
      if (error) throw error;
      toast.success(`Saved "${name}"`);
      qc.invalidateQueries({ queryKey: ["analytics-presets"] });
    } catch (e) { toast.error("Save failed", { description: (e as Error).message }); }
  };
  const applyPreset = (p: Preset) => {
    const f = p.filters ?? {};
    if (f.tenantSlug) setTenantSlug(f.tenantSlug);
    if (f.range) setRange(f.range);
    if (typeof f.routeFilter === "string") setRouteFilter(f.routeFilter);
    if (typeof f.variantFilter === "string") setVariantFilter(f.variantFilter);
    if (f.eventFilter) setEventFilter(f.eventFilter);
    toast.success(`Applied "${p.name}"`);
  };
  const removePreset = async (id: string) => {
    await supabase.from("analytics_filter_presets" as never).delete().eq("id", id);
    qc.invalidateQueries({ queryKey: ["analytics-presets"] });
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2 font-display text-base">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" /> Tenant analytics
            <Badge variant="outline" className="ml-2 text-[10px]">CTA · form · view events</Badge>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-6">
          <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider">Tenant</Label>
            <Select value={tenantSlug} onValueChange={setTenantSlug}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All tenants</SelectItem>
                {tenants.map((t: TenantBranding) => (
                  <SelectItem key={t.slug} value={t.slug}>{t.name} ({t.slug})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider">Range</Label>
            <Select value={range} onValueChange={(v: any) => setRange(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider">Event</Label>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All tracked</SelectItem>
                {TRACKED_EVENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider">Route contains</Label>
            <Input value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} placeholder="e.g. /pricing" />
          </div>
          <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider">Variant / CTA</Label>
            <Input value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)} placeholder="e.g. get-started" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}><Filter className="mr-1 h-3.5 w-3.5" />Apply</Button>
            <Button size="sm" onClick={exportCsv}><Download className="mr-1 h-3.5 w-3.5" />CSV</Button>
          </div>
        </div>

        {/* Presets */}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Saved presets</span>
          {presets.length === 0 && <span className="text-xs text-muted-foreground">— none yet</span>}
          {presets.map((p) => (
            <div key={p.id} className="flex items-center gap-1 rounded-full border bg-muted/30 pl-2.5 pr-1 py-0.5">
              <button className="flex items-center gap-1 text-xs" onClick={() => applyPreset(p)}>
                <Bookmark className="h-3 w-3 text-primary" />{p.name}
              </button>
              <button className="ml-1 rounded p-0.5 text-muted-foreground hover:text-destructive" onClick={() => removePreset(p.id)}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={savePreset}>
            <BookmarkPlus className="mr-1 h-3 w-3" />Save current
          </Button>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid gap-3 md:grid-cols-5">
        <Kpi icon={Eye} label="Page views" value={summary.views} tone="slate" />
        <Kpi icon={MousePointerClick} label="CTA clicks" value={summary.cta} tone="emerald" />
        <Kpi icon={Send} label="Form submits" value={summary.forms} tone="indigo" />
        <Kpi icon={Sparkles} label="Sessions" value={summary.sessions} tone="rose" />
        <Kpi icon={Sparkles} label="Conversion" value={`${summary.conv}%`} tone="amber" />
      </div>

      {/* Time series with drill-down */}
      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium">Events over time</div>
          <div className="text-[10px] text-muted-foreground">Click a day to drill into individual events</div>
        </div>
        <div className="h-72">
          {isLoading ? (
            <div className="grid h-full place-items-center text-xs text-muted-foreground">Loading…</div>
          ) : series.length === 0 ? (
            <div className="grid h-full place-items-center text-xs text-muted-foreground">No events in this window.</div>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={series} onClick={(e: any) => { if (e?.activeLabel) drillDown(e.activeLabel); }} style={{ cursor: "pointer" }}>
                <defs>
                  <linearGradient id="gCta" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gForm" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gView" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#94a3b8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="views" stroke="#94a3b8" fill="url(#gView)" />
                <Area type="monotone" dataKey="cta" stroke="#10b981" fill="url(#gCta)" />
                <Area type="monotone" dataKey="forms" stroke="#6366f1" fill="url(#gForm)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Hourly + breakdowns */}
      <div className="grid gap-3 lg:grid-cols-3">
        <Card className="p-4">
          <div className="mb-2 text-sm font-medium">By hour of day</div>
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={hourly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <RBar dataKey="events" fill="#0f3d2e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <div className="mb-2 text-sm font-medium">Top routes</div>
          <div className="space-y-1.5">
            {topRoutes.length === 0 && <div className="text-xs text-muted-foreground">No data.</div>}
            {topRoutes.map(([path, n]) => (
              <div key={path} className="flex items-center gap-3 text-xs">
                <div className="flex-1 truncate font-mono">{path}</div>
                <Bar value={n} max={topRoutes[0]?.[1] ?? 1} color="#10b981" />
                <div className="w-10 text-right tabular-nums">{n}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="mb-2 text-sm font-medium">Top variants / CTAs</div>
          <div className="space-y-1.5">
            {topVariants.length === 0 && <div className="text-xs text-muted-foreground">No variant metadata captured yet.</div>}
            {topVariants.map(([v, n]) => (
              <div key={v} className="flex items-center gap-3 text-xs">
                <div className="flex-1 truncate">{v}</div>
                <Bar value={n} max={topVariants[0]?.[1] ?? 1} color="#6366f1" />
                <div className="w-10 text-right tabular-nums">{n}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Drill-down modal */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDetailEvent(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" /> Events on {drill?.day}
            <Badge variant="secondary" className="ml-1 text-[10px]">{drill?.events.length ?? 0}</Badge>
          </DialogTitle>
          <div className="grid gap-3 md:grid-cols-[1fr_minmax(0,320px)] max-h-[70vh]">
            <ScrollArea className="max-h-[70vh] pr-2">
              <div className="divide-y">
                {drill?.events.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setDetailEvent(e)}
                    className={`flex w-full items-start gap-3 py-2 text-left text-xs hover:bg-muted/40 ${detailEvent?.id === e.id ? "bg-primary/5" : ""}`}
                  >
                    <div className="w-16 shrink-0 font-mono text-[10px] text-muted-foreground">
                      {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">{e.event_name}</Badge>
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-mono">{e.path ?? "—"}</span>
                      {e.properties?.variant && <span className="ml-1 text-muted-foreground">· {e.properties.variant}</span>}
                    </div>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                  </button>
                ))}
                {!drill?.events.length && <div className="p-4 text-center text-xs text-muted-foreground">No events.</div>}
              </div>
            </ScrollArea>
            <Card className="p-3 text-xs">
              {detailEvent ? (
                <>
                  <div className="mb-2 font-medium">{detailEvent.event_name}</div>
                  <Kv k="path" v={detailEvent.path ?? "—"} />
                  <Kv k="session" v={detailEvent.session_id ?? "—"} />
                  <Kv k="when" v={new Date(detailEvent.created_at).toLocaleString()} />
                  <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">Properties</div>
                  <pre className="mt-1 max-h-[280px] overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px]">
                    {JSON.stringify(detailEvent.properties ?? {}, null, 2)}
                  </pre>
                </>
              ) : (
                <div className="grid h-full place-items-center text-muted-foreground">
                  Select an event to inspect its payload.
                </div>
              )}
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3 border-b py-1 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate text-right font-mono">{v}</span>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: any; label: string; value: number | string; tone: "slate" | "emerald" | "indigo" | "amber" | "rose" }) {
  const map = {
    slate: "from-slate-500/10 to-slate-500/0 text-slate-600",
    emerald: "from-emerald-500/15 to-emerald-500/0 text-emerald-600",
    indigo: "from-indigo-500/15 to-indigo-500/0 text-indigo-600",
    amber: "from-amber-500/15 to-amber-500/0 text-amber-600",
    rose: "from-rose-500/15 to-rose-500/0 text-rose-600",
  } as const;
  return (
    <Card className={`bg-gradient-to-br ${map[tone]} p-4`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="mt-1 font-display text-3xl">{value}</div>
    </Card>
  );
}

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = Math.max(2, Math.round((value / Math.max(1, max)) * 100));
  return (
    <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}
